// src/components/Layout.tsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { navigate, getPathname } from '../utils/navigation';
import { useAuth } from '../context/AuthContext';
import { XPDisplay } from './XPDisplay';
import { XPModal } from './XPModal';
import { useFeedback } from '../context/FeedbackContext';
// Use Convex-based settings hook for real-time sync (replaces SettingsContext for sidebar state)
import { useUserSettings } from '../hooks/useUserSettings';

import { usePageHeader } from '../context/PageHeaderContext';

import { createLogger } from '../utils/logger-ssr-safe';
import { ConsentBanner } from './privacy/ConsentBanner';
import { CacheDebugPanel } from './CacheDebugPanel';
import { NotificationBell } from './notifications/NotificationBell';
import { NotificationPanel } from './notifications/NotificationPanel';
// MIGRATION: Using ConvexNotificationContext instead of legacy NotificationContext
import { useNotifications } from '../context/ConvexNotificationContext';
import { useAdminSettings } from '../context/AdminSettingsContext';
import { MobileMenuProvider } from '../context/MobileMenuContext';

// No custom window properties needed - Stack Auth handles everything

const logger = createLogger({ module: 'Layout' });

// TODO [VIKE]: Add children prop for Vike migration
// In Vike, children are passed from +Layout.tsx, not from Outlet
interface LayoutProps {
  children?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {

  const { user, loading: authLoading } = useAuth();
  const { isAdmin: isUserAdmin } = useAdminSettings();
  const { showFeedback } = useFeedback();

  // Use Convex-based settings hook - provides real-time sync without race conditions
  // The sidebar state is now managed directly by Convex subscriptions
  const { settings, toggleSidebar, isLoading: settingsLoading } = useUserSettings();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Track if tour is active via body class (set by TourOverlay)
  const [isTourActive, setIsTourActive] = useState(false);

  // Check for tour-active body class
  useEffect(() => {
    const checkTourActive = () => {
      setIsTourActive(document.body.classList.contains('tour-active'));
    };

    // Initial check
    checkTourActive();

    // Use MutationObserver to watch for class changes on body
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkTourActive();
        }
      });
    });

    observer.observe(document.body, { attributes: true });

    return () => observer.disconnect();
  }, []);
  const [isXPModalOpen, setIsXPModalOpen] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 1024 : false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Track current pathname for active link highlighting
  // TODO [VIKE]: In Vike, use pageContext.urlPathname instead
  const [currentPath, setCurrentPath] = useState(getPathname());

  // Sidebar collapsed state comes directly from Convex settings
  // No local state needed - Convex provides real-time sync
  const sidebarCollapsed = settings.sidebarCollapsed;

  const { header } = usePageHeader();

  // Notification context for page-specific indicators
  const { getUnreadCountForPage, getTotalUnreadCount } = useNotifications();

  // Admin settings
  const adminSettings = useAdminSettings();

  // Define navigation items (static, moved up for memoization)
  const navigationItems = useMemo(() => [
    { name: 'Home', path: '/home', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { name: 'Retrospectives', path: '/retrospectives', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
    { name: 'Resumes', path: '/resumes', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { name: 'Job Search', path: '/job-search', icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
    { name: 'Profile', path: '/profile', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
    { name: 'Timeline', path: '/timeline', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
    { name: 'Files', path: '/files', icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z' },
  ], []);

  // Admin navigation items if user is an admin
  const adminNavigationItems = useMemo(() => isUserAdmin ? [
    { name: 'Career Focuses', path: '/admin/career-focuses', icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
    { name: 'Notification Test', path: '/admin/notifications', icon: 'M15 17h5l-5 5v-5zM4 2v20l5-5h11V2H4z' },
    { name: 'Notification Analytics', path: '/admin/notification-analytics', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
    { name: 'Users', path: '/admin/users', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
    { name: 'Emails', path: '/admin/emails', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
    { name: 'GDPR Requests', path: '/admin/gdpr-requests', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
    { name: 'Statsig Demo', path: '/statsig-demo', icon: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z' }
  ] : [], [isUserAdmin]);

  // Memoize unread counts to prevent expensive recalculation on every render
  // This reduces 26+ function calls to just 1 per notification change
  const unreadCountsPerPage = useMemo(() => {
    const counts: Record<string, number> = {};
    // Pre-calculate counts for all navigation items
    const allPaths = [
      ...navigationItems.map(item => item.path),
      ...adminNavigationItems.map(item => item.path)
    ];
    allPaths.forEach(path => {
      counts[path] = getUnreadCountForPage(path);
    });
    return counts;
  }, [navigationItems, adminNavigationItems, getUnreadCountForPage]);

  // Add event listener for opening XP modal from other components
  useEffect(() => {
    const handleOpenXPModal = () => {
      setIsXPModalOpen(true);
    };

    window.addEventListener('openXPModal', handleOpenXPModal);
    
    return () => {
      window.removeEventListener('openXPModal', handleOpenXPModal);
    };
  }, []);

  // Update current path and close mobile menu when route changes
  // TODO [VIKE]: In Vike, route changes happen at the page level, not in layout
  // This logic might need to move to a global navigation listener or be removed entirely
  useEffect(() => {
    const handlePathChange = () => {
      const pathname = getPathname();
      setCurrentPath(pathname);
      setIsMobileMenuOpen(false);
    };

    // Listen for popstate (back/forward) and pushstate (navigation) events
    window.addEventListener('popstate', handlePathChange);

    // Initial check
    handlePathChange();

    return () => {
      window.removeEventListener('popstate', handlePathChange);
    };
  }, []);

  // Detect mobile devices
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle toggle collapse - uses Convex mutation directly
  // No optimistic updates needed - Convex handles this with real-time subscriptions
  const handleToggleCollapse = useCallback(async () => {
    try {
      await toggleSidebar();
    } catch (error) {
      logger.error('Error toggling sidebar:', error);
    }
  }, [toggleSidebar]);

  // Simple navigation handler - Stack Auth handles MFA completely
  // Note: Convex handles data prefetching automatically via real-time subscriptions
  const handleNavigation = useCallback((path: string, e?: React.MouseEvent) => {
    const startTime = performance.now();
    console.log('🔘 Navigation click started', { path, currentPath, timestamp: startTime });

    // If this is a direct click (not from the React Router context)
    if (e) {
      e.preventDefault();
      const preventTime = performance.now();
      console.log('⏱️ preventDefault took', { ms: preventTime - startTime });
    }

    // Skip navigation if already on the target page (prevents expensive re-renders)
    if (currentPath === path) {
      console.log('⚠️ Already on target page, skipping navigation', { path });
      return;
    }

    // Set navigating state FIRST to give immediate visual feedback
    // This must happen synchronously before navigate() is called
    setIsNavigating(true);

    // Use requestAnimationFrame to ensure the loading state renders before navigation starts
    // This provides instant visual feedback to the user
    requestAnimationFrame(() => {
      // Navigate immediately - no legacy MFA cleanup needed
      // Stack Auth manages its own authentication state
      const beforeNavigate = performance.now();
      navigate(path);
      const afterNavigate = performance.now();

      console.log('✅ Navigation call completed', {
        path,
        navigateMs: afterNavigate - beforeNavigate,
        totalMs: afterNavigate - startTime
      });

      // Reset navigating state after a short delay (navigation will unmount this component anyway)
      setTimeout(() => setIsNavigating(false), 100);
    });
  }, [currentPath]);

  // TODO [VIKE]: In Vike, authentication redirects should happen at the page level using onBeforeRender
  // This pattern will be replaced with Vike's guard system
  // Handle redirect for unauthenticated users
  // Use a ref to track if we've already checked auth to prevent double redirects
  const authCheckedRef = React.useRef(false);

  useEffect(() => {
    // Only redirect if:
    // 1. Auth has finished loading
    // 2. There's no user
    // 3. We haven't already processed this auth state (prevents race conditions during navigation)
    if (!authLoading && !user && !authCheckedRef.current) {
      // Small delay to allow auth state to stabilize after navigation
      // This prevents false redirects when navigating between authenticated pages
      const timeoutId = setTimeout(() => {
        // Double-check user is still null after delay
        if (!user) {
          authCheckedRef.current = true;
          navigate('/');
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }

    // Reset the check flag when user becomes available
    if (user) {
      authCheckedRef.current = false;
    }
  }, [user, authLoading]);

  // Don't render if not authenticated (after loading completes)
  // Note: We removed the authLoading skeleton check that was causing white flash during navigation
  // The providers persist across navigations, so we don't need to show skeleton every time Layout remounts
  // Show loading state while auth is initializing to prevent flash
  if (authLoading) {
    return null;
  }

  if (!user) {
    return null;
  }

  // Navigation items are now defined via useMemo at the top of the component

  // Use local state for responsive UI
  const sidebarWidth = sidebarCollapsed ? 'w-20' : 'w-64';
  const mainMargin = sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64';

  return (
    <MobileMenuProvider
      isOpen={isMobileMenuOpen}
      toggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      open={() => setIsMobileMenuOpen(true)}
      close={() => setIsMobileMenuOpen(false)}
      unreadCount={getTotalUnreadCount()}
    >
      <div className="h-screen w-full overflow-hidden bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
        {/* Page Header - fixed at top, respecting sidebar width on desktop, with safe area support for PWA */}
        {header && (
          <div
            className={`fixed right-0 z-50 left-0 ${sidebarCollapsed ? 'lg:left-20' : 'lg:left-64'} transition-all duration-200 h-[72px]`}
            style={{
              top: 'max(env(safe-area-inset-top, 0px), 0px)',
              paddingTop: 'env(safe-area-inset-top, 0px)'
            }}>
            {header}
          </div>
        )}

      {/* Layout Container */}
      <div className="flex h-full w-full relative">
        {/* Sidebar - fixed position for desktop, sliding for mobile */}
        <nav
          data-tour-target="sidebar-nav"
          className={`
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          fixed
          ${sidebarWidth} h-screen
          shrink-0
          transition-all duration-200 ease-in-out
          bg-white dark:bg-gray-950 text-gray-800 dark:text-white
          shadow-md dark:shadow-gray-900/50
          flex flex-col
          z-40
          top-0
          left-0
          overflow-hidden
          will-change-transform
        `}>
        <div className="grow overflow-y-auto overflow-x-hidden sidebar-scrollbar">
          <div className={`p-4 ${sidebarWidth}`}>
            {/* Mobile-only close button for better UX */}
            <div className="lg:hidden flex justify-between items-center mb-4">
              <div className="flex items-center">
                <div className="relative">
                  <img src="/logo.svg" alt="CareerCard Logo" className="h-8 w-8 mr-2" />
                  <div className="absolute -bottom-1 -right-1 bg-blue-500 dark:bg-blue-600 text-white text-[8px] px-1 rounded-sm font-bold">BETA</div>
                </div>
                <h1 className="text-2xl font-bold truncate">Career Card</h1>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700"
                aria-label="Close menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Desktop-only title with collapse button */}
            <div className="hidden lg:flex items-center justify-between mb-4">
              <div className={`flex items-center ${sidebarCollapsed ? 'justify-center w-full' : ''}`}>
                <div className="relative">
                  <img src="/logo.svg" alt="CareerCard Logo" className={`${sidebarCollapsed ? 'h-10 w-10' : 'h-8 w-8 mr-2'}`} />
                  <div className={`absolute -bottom-1 -right-1 bg-blue-500 dark:bg-blue-600 text-white ${sidebarCollapsed ? 'text-[9px]' : 'text-[8px]'} px-1 rounded-sm font-bold`}>BETA</div>
                </div>
                <h1 className={`text-2xl font-bold truncate ${sidebarCollapsed ? 'hidden' : 'block'}`}>
                  Career Card
                </h1>
              </div>
              {!sidebarCollapsed && (
                <button
                  onClick={handleToggleCollapse}
                  className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                  aria-label="Collapse sidebar"
                >
                  <svg
                    className="w-5 h-5 transform transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
              )}
            </div>
            {sidebarCollapsed && (
              <div className="hidden lg:flex justify-center mb-4">
                <button
                  onClick={handleToggleCollapse}
                  className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                  aria-label="Expand sidebar"
                >
                  <svg
                    className="w-5 h-5 transform rotate-180 transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
              </div>
            )}
            {/* XP Display and Notification Bell */}
            {sidebarCollapsed ? (
              // Collapsed mode: Notification bell above XP display, vertically stacked
              (<div className="mb-4 w-full flex flex-col items-center space-y-2">
                <NotificationBell 
                  onClick={() => setIsNotificationPanelOpen(true)} 
                  collapsedMode={true}
                />
                <XPDisplay onClick={() => setIsXPModalOpen(true)} collapsedMode={sidebarCollapsed} />
              </div>)
            ) : (
              // Expanded mode: Notification bell and XP display on same row
              (<div className="mb-4 w-full flex items-center justify-between">
                <XPDisplay onClick={() => setIsXPModalOpen(true)} collapsedMode={sidebarCollapsed} />
                <NotificationBell 
                  onClick={() => setIsNotificationPanelOpen(true)} 
                  collapsedMode={false}
                />
              </div>)
            )}
            {/* Navigation Items */}
            <ul data-tour-target={isMobile ? 'mobile-nav' : 'sidebar-nav'} className="w-full space-y-1">
              {navigationItems.map((item) => (
                <li key={item.name} className="w-full">
                  <button
                    type="button"
                    onMouseDown={() => {
                      const mouseDownTime = performance.now();
                      console.log('🖱️ Mouse down on nav button', { path: item.path, timestamp: mouseDownTime });
                    }}
                    onClick={(e) => handleNavigation(item.path, e)}
                    disabled={isNavigating}
                    className={`flex items-center p-3 rounded transition-colors duration-200 w-full
                      ${currentPath === item.path
                        ? 'bg-gray-200 dark:bg-gray-800'
                        : ''}
                      ${isNavigating
                        ? 'opacity-50 cursor-wait'
                        : 'hover:bg-gray-200 dark:hover:bg-gray-800 active:bg-gray-300 dark:active:bg-gray-700'
                      }
                      group
                    `}
                  >
                    <div className={`relative ${sidebarCollapsed ? 'block' : 'hidden'}`}>
                      <svg
                        className="w-6 h-6 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                      </svg>
                      {/* Desktop collapsed mode: show unread dot as overlay badge on icon */}
                      {(unreadCountsPerPage[item.path] || 0) > 0 && (
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                    <span className={`${sidebarCollapsed ? 'hidden' : 'flex items-center justify-between w-full'}`}>
                      <span>{item.name}</span>
                      {/* Desktop expanded mode: show unread dot next to page name */}
                      {(unreadCountsPerPage[item.path] || 0) > 0 && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full shrink-0"></div>
                      )}
                    </span>
                    {sidebarCollapsed && (
                      <div className="fixed left-20 ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-950 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        {item.name}
                      </div>
                    )}
                  </button>
                </li>
              ))}
            </ul>

            {/* Admin Navigation Items */}
            {adminNavigationItems.length > 0 && (
              <ul className="w-full space-y-1 mt-4">
                {!sidebarCollapsed && (
                  <li className="pt-2 pb-1">
                    <div className="text-xs uppercase text-gray-500 dark:text-gray-400 px-3">Admin</div>
                  </li>
                )}
                {adminNavigationItems.map((item) => (
                  <li key={item.name} className="w-full">
                    <button
                      type="button"
                      onClick={(e) => handleNavigation(item.path, e)}
                      disabled={isNavigating}
                      className={`flex items-center p-3 rounded transition-colors duration-200 w-full
                        ${currentPath === item.path
                          ? 'bg-gray-200 dark:bg-gray-800'
                          : ''}
                        ${isNavigating
                          ? 'opacity-50 cursor-wait'
                          : 'hover:bg-gray-200 dark:hover:bg-gray-800 active:bg-gray-300 dark:active:bg-gray-700'
                        }
                        group
                      `}
                    >
                      <svg
                        className={`w-6 h-6 shrink-0 ${sidebarCollapsed ? 'block' : 'hidden'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                      </svg>
                      <span className={`${sidebarCollapsed ? 'hidden' : 'block'}`}>
                        {item.name}
                      </span>
                      {sidebarCollapsed && (
                        <div className="fixed left-20 ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-950 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                          {item.name}
                        </div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div
          data-tour-target={isMobile ? 'mobile-footer' : 'sidebar-footer'}
          className={`p-4 ${sidebarWidth} shrink-0 space-y-2 ${isMobile ? 'pb-24' : ''}`}
        >
          <button
            onClick={showFeedback}
            className="flex items-center p-3 rounded transition-colors duration-200 w-full group
              hover:bg-gray-200 dark:hover:bg-gray-800 active:bg-gray-300 dark:active:bg-gray-700
            "
          >
            <svg 
              className={`w-6 h-6 shrink-0 ${sidebarCollapsed ? 'block' : 'hidden'}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            <span className={`${sidebarCollapsed ? 'hidden' : 'block'}`}>
              Provide Feedback
            </span>
            {sidebarCollapsed && (
              <div className="fixed left-20 ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                Provide Feedback
              </div>
            )}
          </button>
          <button
            onClick={(e) => handleNavigation('/settings', e)}
            className={`flex items-center p-3 rounded transition-colors duration-200 w-full group
              ${currentPath === '/settings'
                ? 'bg-gray-200 dark:bg-gray-700'
                : ''}
              hover:bg-gray-200 dark:hover:bg-gray-800 active:bg-gray-300 dark:active:bg-gray-700
            `}
          >
            <svg 
              className={`w-6 h-6 shrink-0 ${sidebarCollapsed ? 'block' : 'hidden'}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className={`${sidebarCollapsed ? 'hidden' : 'block'}`}>
              Settings
            </span>
            {sidebarCollapsed && (
              <div className="fixed left-20 ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                Settings
              </div>
            )}
          </button>
        </div>
        </nav>
        {/* Mobile menu backdrop - hidden during tour so UI stays visible */}
        {/* Note: backdrop-blur removed as it was causing issues with fixed sidebar on some browsers */}
        {isMobileMenuOpen && !isTourActive && (
          <div
            className="fixed inset-0 bg-black/60 z-30 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
        {/* Main content area - with left margin for desktop and safe area padding */}
        {/* Brightness filter disabled during tour so UI stays visible */}
        <main
          className={`
            flex-1
            h-full
            ${mainMargin}
            bg-white dark:bg-gray-800 text-black dark:text-white
            transition-all duration-200
            ${isMobileMenuOpen && !isTourActive ? 'brightness-50 lg:brightness-100' : ''}
            overflow-y-auto
            overflow-x-hidden
          `}
          style={{
            paddingTop: header ? 'calc(72px + env(safe-area-inset-top, 0px))' : '0'
          }}
        >
          {/* React Router nested routes render via Outlet */}
          {children || <Outlet />}
        </main>
      </div>
      <XPModal
        isOpen={isXPModalOpen}
        onClose={() => setIsXPModalOpen(false)}
        xpGained={0}
        level={1}
        totalXP={0}
        nextLevelXP={100}
      />
      {/* Privacy Consent Banner */}
      <ConsentBanner />
      {/* Cache Debug Panel - available for admin users */}
      {isUserAdmin && adminSettings.settings.showCacheDebug && <CacheDebugPanel />}
      {/* Notification Panel */}
      <NotificationPanel
        isOpen={isNotificationPanelOpen}
        onClose={() => setIsNotificationPanelOpen(false)}
      />
    </div>
    </MobileMenuProvider>
  );
};

export default Layout;